import { TrashIcon } from '@heroicons/react/outline';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SaveOutlined } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FormPart } from '@modules/shared/components/FormPart';
import { Modal } from '@shared/components/modals/Modal';
import { dealFeesCategories } from '@shared/constants/lists';
import { TextInputField } from '@shared/fields/TextInputField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { Loader } from '@shared/enhancers/withLoader';
import { PageContent } from '@styles/CommonStyles';
import { colorTheme } from '@styles/muiTheme';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { DealFeesModalProps } from '../../interfaces';
import { requestDealCreateDocFee, requestDealDeleteDocFee, requestDealUpdateDocFee } from '../../queries';

const ClearButton = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => (
  <Tooltip
    title="Delete"
    placement="top"
  >
    <IconButton
      disabled={disabled}
      onClick={onClick}
    >
      <TrashIcon
        color={colorTheme.palette.secondary.dark}
        width="20px"
      />
    </IconButton>
  </Tooltip>
);

const SaveButton = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => (
  <Tooltip
    title="Save"
    placement="top"
  >
    <IconButton
      disabled={disabled}
      onClick={onClick}
    >
      <SaveOutlined
        style={{ color: '${colorTheme.palette.secondary.dark}' }}
        width="20px"
      />
    </IconButton>
  </Tooltip>
);

export const DealFeesModal = ({
  handleClose,
  reloadDeal,
  dealId,
  type,
  dealFeesArr,
  additionalFeeArr,
  setFieldValue,
  isLoading,
  dealFeesModalPosition,
  dealSavePreValidation,
}: DealFeesModalProps) => {
  const dispatch = useDispatch();
  const isDealFee = type === 'Deal Fees';
  const maxNoOfFees = 5;
  const key = isDealFee ? 'dealFeeArray' : 'additionalFeeArray';
  const feesArr = isDealFee ? dealFeesArr : additionalFeeArr;

  const { isAdmin, userHasRoles } = useUserAccount();
  const isDocFeeEditable = isDealFee ? isAdmin || userHasRoles([Roles.EDITDOCFEE]) : true;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const handleFeesSave = useCallback(
    async (rowIndex: number) => {
      if (!dealSavePreValidation()) {
        return;
      }

      const dealFeeData = isDealFee ? dealFeesArr?.[rowIndex] : additionalFeeArr?.[rowIndex];
      if (dealFeeData) {
        setLoading(true);
        let resp;
        if (dealFeeData?.id) {
          const updatedDealFee = { ...dealFeeData, amount: dealFeeData?.amount };
          resp = await requestDealUpdateDocFee(dealId, updatedDealFee);
        } else {
          dealFeeData.isAdditionalFee = !isDealFee;
          resp = await requestDealCreateDocFee(dealId, dealFeeData);
        }
        if (resp?.status === 200 || resp?.status === 204) {
          setData();
          reloadDeal();
          dispatch(setLastCommentsRefresh(new Date().toISOString()));
        }
        setLoading(false);
      } else {
        Notistack.generalError('Category and Amount fields are required.');
      }
    },
    [dealFeesArr, additionalFeeArr, isDealFee]
  );

  const handleDealFeesDelete = useCallback(
    async (rowIndex: number, rowId?: string) => {
      if (!dealSavePreValidation()) {
        return;
      }
      if (rowId) {
        setLoading(true);
        const resp = await requestDealDeleteDocFee(dealId, rowId);
        if (resp?.status === 204) {
          await setFieldValue?.(`${key}][${rowIndex}].amount`, 0);
          await setFieldValue?.(`${key}][${rowIndex}].category`, '');
          await setFieldValue?.(`${key}][${rowIndex}].description`, '');
          setData();
          reloadDeal();
        }
        setLoading(false);
      }
    },
    [isDealFee]
  );

  const getFieldFormConfig = useCallback(
    (index: number, currentRowId?: string, enableField = true, loading = false) => {
      const fieldConfig = [
        {
          width: '15%',
          label: `Fee ${index + 1}`,
          type: 'label',
        },
        {
          width: '25%',
          name: `[${key}][${index}].category`,
          label: 'Category',
          options: dealFeesCategories,
          disabled: !enableField,
          component: isDealFee ? TextInputField : SelectAutocompleteField,
        },
        {
          width: '25%',
          name: `[${key}][${index}].description`,
          label: 'Description',
          disabled: !enableField,
          component: TextInputField,
        },
        {
          width: '25%',
          name: `[${key}][${index}].amount`,
          label: 'Amount',
          prefix: '$',
          decimalScale: 2,
          disabled: !enableField,
          component: NumberInputField,
        },
        {
          width: '5%',
          component: ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) =>
            enableField ? (
              <SaveButton
                disabled={disabled}
                onClick={onClick}
              />
            ) : null,
          onClick: () => handleFeesSave(index),
          disabled: loading,
        },
        {
          width: '5%',
          component: ({ onClick, rowId, disabled }: { onClick: () => void; rowId: string; disabled: boolean }) =>
            !!rowId && isAdmin ? (
              <ClearButton
                disabled={disabled}
                onClick={onClick}
              />
            ) : null,
          onClick: () => handleDealFeesDelete(index, currentRowId),
          rowId: currentRowId,
          disabled: loading,
        },
      ];

      return fieldConfig;
    },
    [isAdmin, isDealFee, handleFeesSave, handleDealFeesDelete]
  );

  const getFormFields = useMemo(() => {
    const retVal = [];
    for (let i = 0; i < maxNoOfFees; i++) {
      const currentDealFees = feesArr?.[i]?.id;
      const isDocFee = feesArr?.[i]?.isDocFee;
      const enableField = isDocFee ? isDocFeeEditable : true;
      const fieldConfig = getFieldFormConfig(i, currentDealFees, enableField, loading);
      retVal.push(fieldConfig);
    }
    return retVal;
  }, [feesArr, isDocFeeEditable, getFieldFormConfig, loading]);

  const setData = () => {
    if (isDealFee) {
      const docFeeTotal = dealFeesArr?.reduce((acc, obj) => acc + obj?.amount ?? 0, 0);
      setFieldValue?.('financeConditionItem.docFeeTotal', docFeeTotal);
    } else {
      const additionalFeesTotal = additionalFeeArr?.reduce((acc, obj) => acc + obj?.amount ?? 0, 0);
      setFieldValue?.('financeConditionItem.additionalFeesTotal', additionalFeesTotal);
    }
  };

  const onCloseClick = useCallback((event?: object, reason?: string) => {
    if (reason !== 'backdropClick') {
      handleClose();
    }
  }, []);

  return (
    <Modal
      open
      hideBackdrop
      resetPosition={dealFeesModalPosition}
      title={type}
      dialogWidth="1000px"
      dialogHeight="370px"
      onClose={onCloseClick}
    >
      {loading ? (
        <Loader minHeight="auto" />
      ) : (
        <PageContent style={{ padding: '0 20px' }}>
          <Grid container>
            <Grid
              item
              md={12}
            >
              {getFormFields.map((config) => (
                <FormPart
                  key={`formPart-${config[0].label}}`}
                  config={config}
                />
              ))}
            </Grid>
          </Grid>
        </PageContent>
      )}
    </Modal>
  );
};
